import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db, isFirebaseConfigured, collections } from "./firebase.js";
import { addDays, getDayName, toDateKey } from "../utils/admin.js";

const col = (name) => collection(db, collections[name]);

const mapSnapshot = (snap) =>
  snap.docs.map((item) => ({ ...item.data(), id: item.id }));

export async function saveUser(user) {
  if (!isFirebaseConfigured() || !user?.uid) return;
  await setDoc(
    doc(db, collections.users, user.uid),
    { ...user, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

export async function getUserAddresses(uid) {
  if (!isFirebaseConfigured() || !uid) return [];
  const snap = await getDocs(
    query(col("addresses"), where("userId", "==", uid)),
  );
  return mapSnapshot(snap);
}

export function subscribeToUserAddresses(uid, onData, onError) {
  if (!isFirebaseConfigured() || !uid) return () => {};
  const q = query(col("addresses"), where("userId", "==", uid));
  return onSnapshot(
    q,
    (snap) => onData(mapSnapshot(snap)),
    (error) => onError?.(error),
  );
}

export function subscribeToUserProfile(uid, onData, onError) {
  if (!isFirebaseConfigured() || !uid) return () => {};
  return onSnapshot(
    doc(db, collections.users, uid),
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (error) => onError?.(error),
  );
}

export async function saveAddress(uid, address) {
  if (!uid) throw new Error("Please log in before saving an address.");

  if (!isFirebaseConfigured()) {
    return {
      ...address,
      id: address.id || `address-${Date.now()}`,
      userId: uid,
    };
  }

  const ref = address.id
    ? doc(db, collections.addresses, address.id)
    : doc(col("addresses"));

  // Don't send id: undefined to Firestore
  const { id: _id, ...addressData } = address || {};

  await setDoc(
    ref,
    {
      ...addressData,
      userId: uid,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  return {
    id: ref.id,
    ...addressData,
    userId: uid,
  };
}

export async function deleteAddress(id) {
  if (isFirebaseConfigured() && id) {
    await deleteDoc(doc(db, collections.addresses, id));
  }
}

export async function createSchedule(uid, draft) {
  if (!uid) throw new Error("You must be logged in to create a schedule.");
  if (!isFirebaseConfigured())
    return { id: `schedule-${Date.now()}`, ...draft, userId: uid };
  const ref = await addDoc(col("schedules"), {
    ...draft,
    userId: uid,
    status: "active",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { ...draft, id: ref.id, userId: uid };
}

export async function createOrder(uid, order) {
  if (!uid) throw new Error("You must be logged in to create an order.");
  if (!isFirebaseConfigured())
    return { id: `order-${Date.now()}`, ...order, userId: uid };
  const ref = await addDoc(col("orders"), {
    ...order,
    userId: uid,
    createdAt: serverTimestamp(),
  });
  return { ...order, id: ref.id, userId: uid };
}

export async function getSchedule(id) {
  if (!isFirebaseConfigured() || !id) return null;
  const snap = await getDoc(doc(db, collections.schedules, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateSchedule(id, data) {
  if (isFirebaseConfigured() && id) {
    await updateDoc(doc(db, collections.schedules, id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }
}

export function subscribeToUserNotifications(uid, onData, onError) {
  if (!isFirebaseConfigured() || !uid) return () => {};
  const q = query(col("notifications"), where("userId", "==", uid));
  return onSnapshot(
    q,
    (snap) => onData(mapSnapshot(snap)),
    (error) => onError?.(error),
  );
}

export function subscribeToOrders(uid, onData, onError) {
  if (!isFirebaseConfigured() || !uid) return () => {};
  const q = query(col("orders"), where("userId", "==", uid));
  return onSnapshot(
    q,
    (snap) => onData(mapSnapshot(snap)),
    (error) => onError?.(error),
  );
}

export async function updateOrdersForSchedule(
  uid,
  scheduleId,
  status,
  fromDate,
  toDate,
) {
  if (!isFirebaseConfigured() || !uid || !scheduleId) return 0;

  const snap = await getDocs(query(col("orders"), where("userId", "==", uid)));

  const matching = snap.docs.filter((item) => {
    const itemData = item.data();
    if (itemData?.scheduleId !== scheduleId) return false;
    const deliveryDate = itemData?.deliveryDate;
    if (!deliveryDate) return false;
    if (fromDate && deliveryDate < fromDate) return false;
    if (toDate && deliveryDate > toDate) return false;
    const currentStatus = itemData?.status || "Scheduled";
    if (["Delivered", "Cancelled", "Skipped"].includes(currentStatus))
      return false;
    if (!isOrderModifiable(itemData)) return false;
    return true;
  });

  if (!matching.length) return 0;

  const batch = writeBatch(db);
  matching.forEach((item) => {
    batch.update(item.ref, {
      status,
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
  return matching.length;
}


const TERMINAL_ORDER_STATUSES = ["Delivered", "Cancelled", "Skipped"];

export function getSlotStartMinutes(timeSlot = "") {
  const match = String(timeSlot).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return 0;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridiem = match[3].toUpperCase();
  if (meridiem === "AM" && hour === 12) hour = 0;
  if (meridiem === "PM" && hour !== 12) hour += 12;
  return hour * 60 + minute;
}

export function getDeliveryStartDate(order) {
  if (!order?.deliveryDate) return null;
  const date = new Date(`${order.deliveryDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  date.setMinutes(getSlotStartMinutes(order.timeSlot || order.deliveryTimeSlot));
  return date;
}

export function isOrderModifiable(order, now = new Date()) {
  const deliveryStart = getDeliveryStartDate(order);
  if (!deliveryStart) return false;
  return deliveryStart.getTime() - now.getTime() > 24 * 60 * 60 * 1000;
}

export function modificationLockMessage(order, now = new Date()) {
  const deliveryStart = getDeliveryStartDate(order);
  if (!deliveryStart) return "This delivery cannot be modified because its delivery time is unavailable.";
  const hours = Math.max(0, (deliveryStart.getTime() - now.getTime()) / 3600000);
  if (hours <= 24) return "This delivery is locked because changes are allowed only more than 24 hours before delivery.";
  return "";
}

export async function updateOrder(id, data) {
  if (!isFirebaseConfigured() || !id) return;
  const ref = doc(db, collections.orders, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error("This order no longer exists. Refresh the deliveries list and try again.");
  }
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
}

export async function updateConcreteOrder(uid, orderId, changes) {
  if (!isFirebaseConfigured() || !uid || !orderId) throw new Error("Unable to identify this delivery.");
  const ref = doc(db, collections.orders, orderId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("This delivery no longer exists. Refresh and try again.");
  const current = snap.data();
  if (current.userId !== uid) throw new Error("You are not allowed to edit this delivery.");
  if (TERMINAL_ORDER_STATUSES.includes(current.status || "Scheduled")) {
    throw new Error("Completed, cancelled, or skipped deliveries cannot be modified.");
  }
  if (!isOrderModifiable(current)) {
    throw new Error("This delivery is inside the 24-hour modification window and cannot be changed.");
  }
  const proposed = { ...current, ...changes };
  if (!isOrderModifiable(proposed)) {
    throw new Error("The requested new delivery date/time would fall inside the 24-hour modification window.");
  }
  await updateDoc(ref, { ...changes, updatedAt: serverTimestamp() });
  return { ...current, ...changes, id: ref.id };
}

export async function updateOrderAndSchedule(uid, scheduleId, scheduleData, options = {}) {
  if (!isFirebaseConfigured() || !uid || !scheduleId) {
    return { updatedOrders: 0, lockedOrders: 0, skippedOrders: 0 };
  }

  const now = options.now instanceof Date ? options.now : new Date();
  const scheduleRef = doc(db, collections.schedules, scheduleId);
  const scheduleSnap = await getDoc(scheduleRef);
  if (!scheduleSnap.exists()) {
    throw new Error("This schedule no longer exists. Refresh and try again.");
  }

  const currentSchedule = { id: scheduleSnap.id, ...scheduleSnap.data() };
  if (currentSchedule.userId !== uid) {
    throw new Error("You are not allowed to edit this schedule.");
  }
  if (currentSchedule.status === "cancelled") {
    throw new Error("This subscription is already stopped and cannot be changed.");
  }

  const nextSchedule = {
    ...scheduleData,
    userId: uid,
    status: scheduleData.status || currentSchedule.status || "active",
    updatedAt: serverTimestamp(),
  };

  const snap = await getDocs(query(col("orders"), where("userId", "==", uid)));
  const matching = snap.docs
    .filter((item) => item.data()?.scheduleId === scheduleId)
    .sort((a, b) =>
      String(a.data()?.deliveryDate || "").localeCompare(
        String(b.data()?.deliveryDate || ""),
      ),
    );

  const recurrenceChanged =
    currentSchedule.frequency !== nextSchedule.frequency ||
    JSON.stringify(currentSchedule.days || []) !== JSON.stringify(nextSchedule.days || []) ||
    currentSchedule.startDate !== nextSchedule.startDate;

  const batch = writeBatch(db);
  let updatedOrders = 0;
  let lockedOrders = 0;
  let skippedOrders = 0;
  const assignedDates = new Set();
  let oneTimeAssigned = false;

  for (const item of matching) {
    const current = item.data();
    const status = current.status || "Scheduled";

    if (TERMINAL_ORDER_STATUSES.includes(status)) continue;

    // Every existing order is protected by the same 24-hour rule. A schedule
    // edit never creates a replacement order and never rewrites a locked one.
    if (!isOrderModifiable(current, now)) {
      lockedOrders += 1;
      continue;
    }

    if (recurrenceChanged && nextSchedule.frequency === "One Time" && oneTimeAssigned) {
      batch.update(item.ref, { status: "Cancelled", updatedAt: serverTimestamp() });
      skippedOrders += 1;
      continue;
    }

    let deliveryDate = current.deliveryDate;

    if (recurrenceChanged) {
      deliveryDate = findNextScheduleDate(
        nextSchedule,
        current.deliveryDate || nextSchedule.startDate || toDateKey(now),
        assignedDates,
        now,
      );

      if (!deliveryDate) {
        // The requested new occurrence would violate the 24-hour rule (or the
        // recurrence could not produce a safe date). Leave this existing
        // order untouched instead of moving it into a locked window.
        lockedOrders += 1;
        continue;
      }
    }

    const quantity = Math.max(1, Number(nextSchedule.quantity || current.quantity || 1));
    const oldQuantity = Math.max(1, Number(current.quantity || 1));
    const currentAmount = Number(current.amount);
    const unitAmount = Number.isFinite(currentAmount) && currentAmount > 0
      ? currentAmount / oldQuantity
      : currentAmount;

    const changes = {
      deliveryDate,
      timeSlot: nextSchedule.deliveryTimeSlot || current.timeSlot || null,
      deliveryTimeSlot:
        nextSchedule.deliveryTimeSlot || current.deliveryTimeSlot || null,
      addressId: nextSchedule.addressId ?? current.addressId ?? null,
      quantity,
      ...(Number.isFinite(unitAmount) && unitAmount > 0
        ? { amount: unitAmount * quantity }
        : {}),
      recurring: nextSchedule.frequency !== "One Time",
      days: Array.isArray(nextSchedule.days) ? nextSchedule.days : [],
      frequency: nextSchedule.frequency || current.frequency || null,
      ...(Array.isArray(nextSchedule.selectedFoods)
        ? { selectedFoods: nextSchedule.selectedFoods }
        : {}),
    };

    if (!isOrderModifiable({ ...current, ...changes }, now)) {
      lockedOrders += 1;
      continue;
    }

    if (deliveryDate) assignedDates.add(deliveryDate);
    if (recurrenceChanged && nextSchedule.frequency === "One Time") oneTimeAssigned = true;

    batch.update(item.ref, { ...changes, updatedAt: serverTimestamp() });
    updatedOrders += 1;
  }

  // The schedule and all eligible existing order instances are committed
  // together. No addDoc/createOrder call is made on this path.
  batch.update(scheduleRef, nextSchedule);
  await batch.commit();

  return { updatedOrders, lockedOrders, skippedOrders };
}

function findNextScheduleDate(schedule, fromDate, usedDates = new Set(), now = new Date()) {
  if (!schedule) return null;

  const startKey = schedule.startDate && schedule.startDate > fromDate
    ? schedule.startDate
    : fromDate;

  if (schedule.frequency === "One Time") {
    return isOrderModifiable(
      { deliveryDate: startKey, timeSlot: schedule.deliveryTimeSlot },
      now,
    )
      ? startKey
      : null;
  }

  const days = Array.isArray(schedule.days) ? schedule.days.filter(Boolean) : [];

  // Daily schedules have no weekday restriction.
  if (schedule.frequency === "Daily") {
    let cursor = new Date(`${startKey}T00:00:00`);
    for (let i = 0; i < 370; i += 1) {
      const key = toDateKey(cursor);
      if (
        !usedDates.has(key) &&
        !isDateBlockedByPause(schedule, key) &&
        isOrderModifiable(
          { deliveryDate: key, timeSlot: schedule.deliveryTimeSlot },
          now,
        )
      ) return key;
      cursor = addDays(cursor, 1);
    }
    return null;
  }

  if (!days.length) return null;

  // Monthly is one selected weekday per calendar month.
  if (schedule.frequency === "Monthly") {
    const wanted = days[0];
    let cursor = new Date(`${startKey}T00:00:00`);
    for (let i = 0; i < 24; i += 1) {
      const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      let candidate = new Date(Math.max(cursor.getTime(), monthStart.getTime()));
      while (candidate <= monthEnd && getDayName(candidate) !== wanted) {
        candidate = addDays(candidate, 1);
      }
      if (candidate <= monthEnd) {
        const key = toDateKey(candidate);
        if (
          !usedDates.has(key) &&
          !isDateBlockedByPause(schedule, key) &&
          isOrderModifiable(
            { deliveryDate: key, timeSlot: schedule.deliveryTimeSlot },
            now,
          )
        ) return key;
      }
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return null;
  }

  let cursor = new Date(`${startKey}T00:00:00`);
  for (let i = 0; i < 370; i += 1) {
    const key = toDateKey(cursor);
    if (
      days.includes(getDayName(cursor)) &&
      !usedDates.has(key) &&
      !isDateBlockedByPause(schedule, key) &&
      isOrderModifiable(
        { deliveryDate: key, timeSlot: schedule.deliveryTimeSlot },
        now,
      )
    ) {
      return key;
    }
    cursor = addDays(cursor, 1);
  }
  return null;
}

function isDateBlockedByPause(schedule, key) {
  if (Array.isArray(schedule.pauseDates) && schedule.pauseDates.includes(key)) return true;
  return Array.isArray(schedule.pauseRanges) && schedule.pauseRanges.some(
    (range) => range?.from && range?.until && key >= range.from && key <= range.until,
  );
}

function toDateKeyLocal(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export async function updateOrderStatus(id, status) {
  if (!isFirebaseConfigured() || !id) return;
  const ref = doc(db, collections.orders, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error("This delivery no longer exists. Refresh your deliveries list and try again.");
  }
  await updateDoc(ref, {
    status,
    updatedAt: serverTimestamp(),
  });
}

export async function getAdminOrders() {
  if (!isFirebaseConfigured() || !db) return [];
  const snap = await getDocs(
    query(col("orders"), orderBy("createdAt", "desc")),
  );
  return mapSnapshot(snap);
}
